import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, useAuth } from "@/App";
import { toast } from "sonner";
import { 
  Globe, Plus, Trash2, Check, X, AlertCircle, Crown, 
  Loader2, ExternalLink, Copy, CheckCircle2, Link2,
  Mail, Send, Instagram, Music2, LinkIcon, Save, Upload, Image as ImageIcon, Power
} from "lucide-react";
import { FaTelegram, FaInstagram, FaVk, FaTiktok, FaYoutube, FaFacebook, FaGlobe } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { motion, AnimatePresence } from "framer-motion";
import Sidebar from "@/components/Sidebar";
import ProFeatureModal from "@/components/ProFeatureModal";
import ConfirmDialog from "@/components/ConfirmDialog";
import { useLanguage } from "@/contexts/LanguageContext";

const SOCIAL_PLATFORMS = [
  { id: "telegram", name: "Telegram", icon: FaTelegram, placeholder: "@username", color: "#ffffff", bgColor: "#26A5E4" },
  { id: "instagram", name: "Instagram", icon: FaInstagram, placeholder: "@username", color: "#ffffff", bgColor: "#E4405F" },
  { id: "vk", name: "VKontakte", icon: FaVk, placeholder: "vk.com/username", color: "#ffffff", bgColor: "#0077FF" },
  { id: "tiktok", name: "TikTok", icon: FaTiktok, placeholder: "@username", color: "#ffffff", bgColor: "#000000" },
  { id: "twitter", name: "X", icon: FaXTwitter, placeholder: "@username", color: "#ffffff", bgColor: "#000000" },
  { id: "youtube", name: "YouTube", icon: FaYoutube, placeholder: "@channel", color: "#ffffff", bgColor: "#FF0000" },
  { id: "facebook", name: "Facebook", icon: FaFacebook, placeholder: "facebook.com/username", color: "#ffffff", bgColor: "#1877F2" },
  { id: "website", name: "Website", icon: FaGlobe, placeholder: "https://example.com", color: "#ffffff", bgColor: "#000000" },
];

export default function Domains() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const isOwner = user?.role === "owner";
  const isStaff = isOwner || user?.role === "admin";
  const [subdomains, setSubdomains] = useState([]);
  const [canAdd, setCanAdd] = useState(false);
  const [deleteSubId, setDeleteSubId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newSubdomain, setNewSubdomain] = useState("");
  const [checking, setChecking] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  
  // Contact info state
  const [contactEmail, setContactEmail] = useState("");
  const [socialLinks, setSocialLinks] = useState({
    telegram: "",
    instagram: "",
    vk: "",
    tiktok: "",
    twitter: "",
    youtube: "",
    facebook: "",
    website: ""
  });
  const [profileDescription, setProfileDescription] = useState("");
  const [artistName, setArtistName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [backgroundUrl, setBackgroundUrl] = useState("");
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [savingContacts, setSavingContacts] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [proModalOpen, setProModalOpen] = useState(false);

  useEffect(() => {
    fetchSubdomains();
    fetchContactInfo();
  }, []);

  // Debounced availability check
  useEffect(() => {
    if (!newSubdomain || newSubdomain.length < 3) {
      setAvailability(null);
      return;
    }

    const timer = setTimeout(() => {
      checkAvailability(newSubdomain);
    }, 300);

    return () => clearTimeout(timer);
  }, [newSubdomain]);

  const fetchSubdomains = async () => {
    try {
      const response = await api.get("/subdomains");
      setSubdomains(response.data.subdomains || []);
      setCanAdd(response.data.can_add);
    } catch (error) {
      toast.error(t('errors', 'loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchContactInfo = async () => {
    try {
      const response = await api.get("/profile/contacts");
      setContactEmail(response.data.contact_email || "");
      setProfileDescription(response.data.profile_description || "");
      setArtistName(response.data.artist_name || "");
      setAvatarUrl(response.data.avatar_url || "");
      setBackgroundUrl(response.data.background_url || "");
      setSocialLinks(response.data.social_links || {
        telegram: "",
        instagram: "",
        vk: "",
        tiktok: "",
        twitter: "",
        youtube: "",
        facebook: "",
        website: ""
      });
    } catch (error) {
      console.error("Failed to fetch contact info", error);
    } finally {
      setContactsLoading(false);
    }
  };

  const saveContactInfo = async () => {
    setSavingContacts(true);
    try {
      await api.put("/profile/contacts", {
        contact_email: contactEmail,
        profile_description: profileDescription,
        artist_name: artistName,
        avatar_url: avatarUrl,
        background_url: backgroundUrl,
        social_links: socialLinks
      });
      toast.success(t('common', 'success'));
    } catch (error) {
      toast.error(t('errors', 'saveFailed'));
    } finally {
      setSavingContacts(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingAvatar(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await api.post("/upload/image", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const uploadedUrl = response.data.url;
      setAvatarUrl(uploadedUrl);
      await api.put("/profile/contacts", {
        contact_email: contactEmail,
        profile_description: profileDescription,
        artist_name: artistName,
        avatar_url: uploadedUrl,
        background_url: backgroundUrl,
        social_links: socialLinks
      });
      toast.success(t('common', 'success'));
    } catch (error) {
      toast.error(t('errors', 'uploadFailed'));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleBackgroundUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingBackground(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await api.post("/upload/image", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const uploadedUrl = response.data.url;
      setBackgroundUrl(uploadedUrl);
      await api.put("/profile/contacts", {
        contact_email: contactEmail,
        profile_description: profileDescription,
        artist_name: artistName,
        avatar_url: avatarUrl,
        background_url: uploadedUrl,
        social_links: socialLinks
      });
      toast.success(t('common', 'success'));
    } catch (error) {
      toast.error(t('errors', 'uploadFailed'));
    } finally {
      setUploadingBackground(false);
    }
  };

  const handleSocialLinkChange = (platform, value) => {
    setSocialLinks(prev => ({
      ...prev,
      [platform]: value
    }));
  };

  const checkAvailability = async (subdomain) => {
    setChecking(true);
    try {
      const response = await api.get(`/subdomains/check/${subdomain}`);
      setAvailability(response.data);
    } catch (error) {
      setAvailability({ available: false, reason: t('errors', 'generic') });
    } finally {
      setChecking(false);
    }
  };

  const createSubdomain = async () => {
    if (!newSubdomain || !availability?.available) return;

    setCreating(true);
    try {
      await api.post("/subdomains", { subdomain: newSubdomain.toLowerCase() });
      toast.success(t('common', 'success'));
      setNewSubdomain("");
      setAvailability(null);
      fetchSubdomains();
    } catch (error) {
      toast.error(typeof (error.response?.data?.detail) === "string" ? error.response.data.detail : t('errors', 'generic'));
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = (subdomain) => {
    const url = `${subdomain}.mus.link`;
    navigator.clipboard.writeText(url);
    setCopiedId(subdomain);
    toast.success(t('common', 'copied'));
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Staff (owner/admin) actions: enable/disable and delete any subdomain
  const toggleSubdomain = async (sub) => {
    try {
      await api.put(`/subdomains/${sub.id}`, { is_active: !sub.is_active });
      toast.success(sub.is_active ? t('common', 'disabled') : t('common', 'enabled'));
      fetchSubdomains();
    } catch (error) {
      toast.error(typeof (error.response?.data?.detail) === "string" ? error.response.data.detail : t('errors', 'generic'));
    }
  };

  const doDeleteSubdomain = async () => {
    const id = deleteSubId;
    setDeleteSubId(null);
    if (!id) return;
    try {
      await api.delete(`/subdomains/${id}`);
      toast.success(t('common', 'success'));
      fetchSubdomains();
    } catch (error) {
      toast.error(typeof (error.response?.data?.detail) === "string" ? error.response.data.detail : t('errors', 'generic'));
    }
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <div className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin"></div>
              <Globe className="w-6 h-6 text-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            </div>
            <p className="text-muted-foreground">{t('common', 'loading')}</p>
          </div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="min-h-screen bg-background">
        <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto">
          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 sm:mb-8"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold">{t('domains', 'title')}</h1>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {t('domains', 'subtitle')}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* PRO badge only */}
              <div className={`px-3 py-1.5 rounded-xl text-xs font-medium ${
                user?.plan === 'pro' 
                  ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-400 border border-blue-500/30' 
                  : 'bg-muted text-muted-foreground'
              }`}>
                <Crown className="w-3 h-3 inline-block mr-1" />
                {user?.plan === 'pro' ? 'PRO' : 'FREE'}
              </div>
            </div>
          </motion.div>

          {/* Add Subdomain Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="p-5 sm:p-6 rounded-2xl panel-card mb-6"
          >
            <h2 className="font-semibold mb-4 flex items-center gap-2">
              <Plus className="w-4 h-4 text-primary" />
              {t('domains', 'addDomain')}
            </h2>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <div className="flex items-center relative">
                  <Input
                    value={newSubdomain}
                    onChange={(e) => setNewSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    placeholder={t('domains', 'domainPlaceholder')}
                    className="rounded-r-none border-r-0 bg-muted/50 lowercase"
                    disabled={!canAdd && !isStaff}
                    maxLength={32}
                  />
                  <div className="h-10 px-3 flex items-center bg-muted border border-l-0 border-border rounded-r-lg text-muted-foreground text-sm">
                    .mus.link
                  </div>

                  {/* Mobile: centered status badge INSIDE the input row */}
                  {newSubdomain.length >= 3 && (
                    <div className="sm:hidden pointer-events-none absolute inset-y-0 left-0 right-[92px] flex items-center justify-center">
                      {checking ? (
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-background/80 backdrop-blur-sm text-xs text-muted-foreground">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          {t('common', 'loading')}
                        </span>
                      ) : availability?.available ? (
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 backdrop-blur-sm text-xs text-emerald-400">
                          <CheckCircle2 className="w-3 h-3" />
                          {t('common', 'active')}
                        </span>
                      ) : availability ? (
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/15 backdrop-blur-sm text-xs text-red-400">
                          <X className="w-3 h-3" />
                          {availability.reason}
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>
                
                {/* Desktop: availability indicator below the input */}
                <AnimatePresence>
                  {newSubdomain.length >= 3 && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="hidden sm:flex absolute left-0 -bottom-6 text-xs items-center gap-1"
                    >
                      {checking ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin" />
                          <span className="text-muted-foreground">{t('common', 'loading')}</span>
                        </>
                      ) : availability?.available ? (
                        <>
                          <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                          <span className="text-emerald-400">{t('common', 'active')}</span>
                        </>
                      ) : availability ? (
                        <>
                          <X className="w-3 h-3 text-red-400" />
                          <span className="text-red-400">{availability.reason}</span>
                        </>
                      ) : null}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              <Button
                onClick={createSubdomain}
                disabled={(!canAdd && !isStaff) || !availability?.available || creating}
                className="bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90 min-w-[120px]"
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-1" />
                    {t('common', 'create')}
                  </>
                )}
              </Button>
            </div>
            
            {/* Limit warning */}
            {!canAdd && !isStaff && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="mt-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-start gap-3"
              >
                <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-amber-400">
                    {t('domains', 'oneSubdomainOnly')}
                  </p>
                </div>
              </motion.div>
            )}

            {/* Instructions */}
            <div className="mt-4 p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
              <h3 className="font-semibold text-sm text-blue-400 mb-2">{t('domains', 'instructions')}</h3>
              <ul className="text-xs text-muted-foreground space-y-1.5">
                <li>• {t('domains', 'step1')}</li>
                <li>• {t('domains', 'step2')}</li>
                <li>• {t('domains', 'step3')}</li>
              </ul>
            </div>
          </motion.div>

          {/* Subdomains List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="font-semibold mb-4 flex items-center gap-2">
              <Link2 className="w-4 h-4 text-muted-foreground" />
              {t('nav', 'domains')}
              <span className="text-xs text-muted-foreground font-normal">({subdomains.length})</span>
            </h2>
            
            {subdomains.length === 0 ? (
              <div className="text-center py-16 rounded-2xl border border-dashed border-border">
                <Globe className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-2">{t('analytics', 'noData')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {subdomains.map((sub, idx) => (
                  <motion.div
                    key={sub.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + idx * 0.05 }}
                    className={`p-4 sm:p-5 rounded-2xl border transition-all ${
                      sub.is_active 
                        ? 'bg-card/50 border-border hover:border-border' 
                        : 'bg-card/30 border-border'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      {/* Domain Info */}
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          sub.is_active 
                            ? 'bg-gradient-to-br from-emerald-500/20 to-teal-500/20' 
                            : 'bg-muted'
                        }`}>
                          <Globe className={`w-5 h-5 ${sub.is_active ? 'text-emerald-400' : 'text-zinc-500'}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`font-mono font-semibold ${!sub.is_active && 'text-zinc-500'}`}>
                              {sub.subdomain}.mus.link
                            </p>
                            {!sub.is_active && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] bg-muted text-muted-foreground">
                                {t('common', 'disabled')}
                              </span>
                            )}
                            {sub.disabled_by_admin && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] bg-red-500/20 text-red-400">
                                {t('domains', 'failed')}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {new Date(sub.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 pl-14 sm:pl-0">
                        {/* Copy button */}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => copyToClipboard(sub.subdomain)}
                          className="h-9 w-9 rounded-xl"
                        >
                          {copiedId === sub.subdomain ? (
                            <Check className="w-4 h-4 text-emerald-400" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>

                        {/* External link */}
                        <a 
                          href={`https://${sub.subdomain}.mus.link`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl">
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </a>

                        <span className="text-xs text-muted-foreground px-2">
                          {t('domains', 'immutable')}
                        </span>

                        {/* Staff (owner/admin) controls: enable/disable + delete */}
                        {isStaff && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => toggleSubdomain(sub)}
                              className={`h-9 rounded-xl text-xs font-medium ${
                                sub.is_active
                                  ? 'border-amber-500/40 text-amber-500 hover:bg-amber-500/10 hover:text-amber-400'
                                  : 'border-emerald-500/40 text-emerald-500 hover:bg-emerald-500/10 hover:text-emerald-400'
                              }`}
                            >
                              <Power className="w-3.5 h-3.5 mr-1.5" />
                              {sub.is_active ? t('common', 'disable') : t('common', 'enable')}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeleteSubId(sub.id)}
                              className="h-9 w-9 rounded-xl text-muted-foreground hover:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Contact Info Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mt-6 p-5 sm:p-6 rounded-2xl panel-card"
          >
            <h2 className="font-semibold mb-4 flex items-center gap-2">
              <Mail className="w-4 h-4 text-primary" />
              {t('settings', 'profile')}
            </h2>
            
            {contactsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : (
              <>
                {/* Avatar & Background Upload */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  {/* Avatar Upload */}
                  <div>
                    <Label className="text-sm mb-2 block">{t('settings', 'avatar')}</Label>
                    <div className="flex items-center gap-3">
                      <div className="relative w-16 h-16 rounded-full bg-muted overflow-hidden flex-shrink-0">
                        {avatarUrl ? (
                          <img 
                            src={avatarUrl.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${avatarUrl}` : avatarUrl}
                            alt="Avatar"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Upload className="w-6 h-6 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarUpload}
                          className="hidden"
                          id="avatar-upload"
                        />
                        <label htmlFor="avatar-upload">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="cursor-pointer"
                            disabled={uploadingAvatar}
                            asChild
                          >
                            <span>
                              {uploadingAvatar ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              ) : (
                                <Upload className="w-4 h-4 mr-2" />
                              )}
                              {t('common', 'upload')}
                            </span>
                          </Button>
                        </label>
                        {avatarUrl && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setAvatarUrl("")}
                            className="ml-2 text-red-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Background Upload */}
                  <div>
                    <Label className="text-sm mb-2 block">{t('settings', 'background')}</Label>
                    <div className="flex items-center gap-3">
                      <div className="relative w-16 h-16 rounded-xl bg-muted overflow-hidden flex-shrink-0">
                        {backgroundUrl ? (
                          <img 
                            src={backgroundUrl.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${backgroundUrl}` : backgroundUrl}
                            alt="Background"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="w-6 h-6 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleBackgroundUpload}
                          className="hidden"
                          id="background-upload"
                        />
                        <label htmlFor="background-upload">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="cursor-pointer"
                            disabled={uploadingBackground}
                            asChild
                          >
                            <span>
                              {uploadingBackground ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              ) : (
                                <Upload className="w-4 h-4 mr-2" />
                              )}
                              {t('common', 'upload')}
                            </span>
                          </Button>
                        </label>
                        {backgroundUrl && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setBackgroundUrl("")}
                            className="ml-2 text-red-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Artist Name */}
                <div className="mb-4">
                  <Label htmlFor="artist_name" className="text-sm mb-2 block">
                    {t('common', 'artist')}
                  </Label>
                  <Input
                    id="artist_name"
                    type="text"
                    value={artistName}
                    onChange={(e) => setArtistName(e.target.value)}
                    placeholder={t('pageBuilder', 'artistPlaceholder')}
                    className="bg-muted/50"
                    maxLength={50}
                  />
                </div>

                {/* Profile Description */}
                <div className="mb-4">
                  <Label htmlFor="profile_description" className="text-sm mb-2 block">
                    {t('settings', 'bio')}
                  </Label>
                  <Textarea
                    id="profile_description"
                    value={profileDescription}
                    onChange={(e) => setProfileDescription(e.target.value)}
                    placeholder={t('settings', 'bioPlaceholder')}
                    className="bg-muted/50 resize-none"
                    rows={3}
                    maxLength={200}
                  />
                </div>
                
                {/* Contact Email */}
                <div className="mb-4">
                  <Label htmlFor="contact_email" className="text-sm mb-2 block">
                    {t('common', 'email')}
                  </Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder={t('auth', 'emailPlaceholder')}
                    className="bg-muted/50"
                  />
                </div>
                
                {/* Social Links */}
                <div className="space-y-3">
                  <Label className="text-sm">{t('settings', 'socialLinks')}</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {SOCIAL_PLATFORMS.map((platform) => {
                      const Icon = platform.icon;
                      return (
                        <div key={platform.id} className="flex items-center gap-2">
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-black border border-white/10">
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                          <Input
                            value={socialLinks[platform.id] || ""}
                            onChange={(e) => handleSocialLinkChange(platform.id, e.target.value)}
                            placeholder={platform.placeholder}
                            className="bg-muted/50 h-9 text-sm"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Save Button */}
                <Button
                  onClick={saveContactInfo}
                  disabled={savingContacts}
                  className="mt-5 bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90"
                >
                  {savingContacts ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {t('common', 'save')}
                </Button>
              </>
            )}
          </motion.div>
        </div>
      </div>

      {/* PRO Feature Modal */}
      <ProFeatureModal
        open={proModalOpen}
        onOpenChange={setProModalOpen}
        featureName="Custom Domains"
      />
      <ConfirmDialog
        open={Boolean(deleteSubId)}
        title={t('admin', 'subdomainDeleteConfirm') || 'Удалить поддомен?'}
        destructive
        confirmText={t('common', 'delete') || 'Удалить'}
        onConfirm={doDeleteSubdomain}
        onCancel={() => setDeleteSubId(null)}
      />
    </Sidebar>
  );
}

